// Whitelist는 이게 최대, 더 이상의 path는 추가되지 않는 것 같음
import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect } from 'react';

import { authBunja } from '@flipster/features/src/shared/auth/state';
import { userRegulationBunja } from '@flipster/features/src/shared/regulation';
import { css } from '@flipster/features/styled-system/css';
import { usePathname, useRouter } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { alert } from '@flipster/ui/Alert';
import segment from '@flipster/utils/segment';
import { VaraEvent } from '@flipster/utils/segment/events/vara';

const spotOnlyWhitelistFilter = [
  /\/(email-signup|forgotPassword|resetPassword|social-signup|about|login|signin|signup|assets|fullchart|pnl-analysis|policies|blog|auth|glossary|usd1-points)/,
  /\/(trade|market|history)\/spot/,
  /\/history\/export/,
  /\/transaction\/(conversion|deposit|withdrawal)/,
  /\/support\/(guide|spot-pairs|proof-of-reserves)/,
  /\/user\/(account|kyc)/,
] as const;

export const useUserRegulationRedirect = () => {
  const router = useRouter();
  const pathname = usePathname();

  const { isCookieLoggedInAtom } = useBunja(authBunja);
  const { isShowAlertAtom, isSpotOnlyFromDataAtom, isVaraTemporaryOpenAtom } = useBunja(userRegulationBunja);
  const isCookieLoggedIn = useAtomValue(isCookieLoggedInAtom);
  const [isShowAlert, setIsShowAlert] = useAtom(isShowAlertAtom);
  const isSpotOnlyFromData = useAtomValue(isSpotOnlyFromDataAtom);
  const isVaraTemporaryOpen = useAtomValue(isVaraTemporaryOpenAtom);
  const overlay = useOverlay();

  useEffect(() => {
    if (!isSpotOnlyFromData) return;

    const isMainPage = pathname === '/';
    const isNotWhitelistPage = !spotOnlyWhitelistFilter.some((filter) => pathname.match(filter));

    function onConfirm() {
      let redirectPath = '/';
      if (pathname === '/market/perpetual') redirectPath = '/market/spot';
      else if (pathname.startsWith('/trade/perpetual')) redirectPath = '/trade/spot/BTCUSDT';
      else if (!isMainPage && isCookieLoggedIn) redirectPath = '/assets';

      setIsShowAlert(true);
      router.replace(redirectPath);
    }

    if (!isShowAlert && isMainPage && isVaraTemporaryOpen) {
      overlay.close('modal');
      alert.show({
        title: 'Coming soon to your region',
        message: (
          <>
            <p>
              Flipster isn’t available in your region yet, but we’re working on it. You can still explore the platform.
            </p>
            <p>
              {'Need help? Please reach out to '}
              <a
                href='mailto:support@flipster.io'
                target='_blank'
                rel='noreferrer'
                className={css({ color: 'content.default.level-2' })}
              >
                support@flipster.io
              </a>
              .
            </p>
          </>
        ),
        confirmText: 'Continue',
        // TODO: [sh] 정식 오픈 후 문구 변경
        // message: `It looks like you're in the United Arab Emirates.
        //   Please visit our UAE site for the best experience.`,
        // confirmText: 'Go to Flipster in UAE',
        onConfirm,
      });
      segment.track(VaraEvent.VaraRestrictionModalView);
      return;
    }
    // 허용된 페이지가 아닌 페이지에 접근하려고 할때마다 노출
    if (!isMainPage && isNotWhitelistPage) {
      overlay.close('modal');
      alert.show({
        title: 'Restricted location',
        message: (
          <p>
            We regret to inform you that some of Flipster services are not currently available in your location. We are
            working hard to increase our list of supported countries. If you have any queries please contact us at
            <a
              href='mailto:support@flipster.io'
              target='_blank'
              rel='noreferrer'
              className={css({ color: 'content.default.level-2' })}
            >
              support@flipster.io
            </a>
          </p>
        ),
        confirmText: 'Go to Flipster.io',
        onConfirm,
      });
      return;
    }
    setIsShowAlert(true);
  }, [
    router,
    pathname,
    isCookieLoggedIn,
    isShowAlert,
    isSpotOnlyFromData,
    setIsShowAlert,
    isVaraTemporaryOpen,
    overlay,
  ]);
};
